import React from 'react';
import { useTranslations } from '../hooks/useTranslations';

const BrandValueMethodologyModal = ({ isOpen, onClose }) => {
  const { t } = useTranslations();

  if (!isOpen) return null;

  const chartData = [
    { label: t('methodology.chart.baseRevenue'), percentage: 45, color: '#8B5CF6' },
    { label: t('methodology.chart.clientBase'), percentage: 20, color: '#06B6D4' },
    { label: t('methodology.chart.digitalPresence'), percentage: 15, color: '#10B981' },
    { label: t('methodology.chart.brandRecognition'), percentage: 12, color: '#F59E0B' },
    { label: t('methodology.chart.otherFactors'), percentage: 8, color: '#EF4444' }
  ];

  // Crear un gráfico circular simple usando CSS
  let cumulativePercentage = 0;
  const chartSegments = chartData.map((item, index) => {
    const startAngle = (cumulativePercentage / 100) * 360;
    cumulativePercentage += item.percentage;
    const endAngle = (cumulativePercentage / 100) * 360;
    
    return {
      ...item,
      startAngle,
      endAngle
    };
  });

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-slate-800/95 backdrop-blur-md rounded-2xl border border-purple-500/30 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-2xl font-bold text-white">
            {t('methodology.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl font-bold transition-colors"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Base Formula */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-white mb-4">
              {t('methodology.baseFormula')}
            </h3>
            <div className="bg-slate-700/50 rounded-lg p-4 border border-cyan-500/30">
              <p className="text-cyan-300 text-lg font-mono text-center">
                {t('methodology.formula')}
              </p>
            </div>
          </div>

          {/* Three Main Components */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-purple-900/30 rounded-xl p-4 border border-purple-500/30">
              <h4 className="text-lg font-semibold text-white mb-2">
                {t('methodology.components.annualRevenue.title')}
              </h4>
              <p className="text-purple-300 text-sm">
                {t('methodology.components.annualRevenue.description')}
              </p>
            </div>
            <div className="bg-cyan-900/30 rounded-xl p-4 border border-cyan-500/30">
              <h4 className="text-lg font-semibold text-white mb-2">
                {t('methodology.components.sectorMultiplier.title')}
              </h4>
              <p className="text-cyan-300 text-sm">
                {t('methodology.components.sectorMultiplier.description')}
              </p>
            </div>
            <div className="bg-green-900/30 rounded-xl p-4 border border-green-500/30">
              <h4 className="text-lg font-semibold text-white mb-2">
                {t('methodology.components.additionalAdjustments.title')}
              </h4>
              <p className="text-green-300 text-sm">
                {t('methodology.components.additionalAdjustments.description')}
              </p>
            </div>
          </div>

          {/* Chart Section */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-white mb-6">
              {t('methodology.weightDistribution')}
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Simple CSS Pie Chart */}
              <div className="flex justify-center">
                <div className="relative w-64 h-64">
                  {/* Background circle */}
                  <div className="w-full h-full rounded-full bg-slate-600"></div>
                  
                  {/* Chart segments - simplified representation */}
                  <div className="absolute inset-0 rounded-full overflow-hidden">
                    <div 
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: `conic-gradient(
                          ${chartData[0].color} 0% ${chartData[0].percentage}%,
                          ${chartData[1].color} ${chartData[0].percentage}% ${chartData[0].percentage + chartData[1].percentage}%,
                          ${chartData[2].color} ${chartData[0].percentage + chartData[1].percentage}% ${chartData[0].percentage + chartData[1].percentage + chartData[2].percentage}%,
                          ${chartData[3].color} ${chartData[0].percentage + chartData[1].percentage + chartData[2].percentage}% ${chartData[0].percentage + chartData[1].percentage + chartData[2].percentage + chartData[3].percentage}%,
                          ${chartData[4].color} ${chartData[0].percentage + chartData[1].percentage + chartData[2].percentage + chartData[3].percentage}% 100%
                        )`
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="space-y-3">
                {chartData.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div 
                        className="w-4 h-4 rounded-full mr-3"
                        style={{ backgroundColor: item.color }}
                      ></div>
                      <span className="text-gray-300 text-sm">{item.label}</span>
                    </div>
                    <span className="text-white font-semibold">{item.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Important Note */}
          <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-start">
              <div className="text-yellow-400 text-2xl mr-3">⚠️</div>
              <div>
                <h4 className="text-yellow-300 font-semibold mb-2">
                  {t('methodology.importantNote.title')}
                </h4>
                <p className="text-yellow-100 text-sm leading-relaxed">
                  {t('methodology.importantNote.description')}
                </p>
              </div>
            </div>
          </div>

          {/* ISO Standard Note */}
          <div className="text-center">
            <p className="text-gray-400 text-xs leading-relaxed">
              {t('methodology.isoNote')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandValueMethodologyModal;